import React, {useState} from 'react';
import {
  Body,
  Button,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import {
  canExtendApplicationExpiration,
  isApplicationExpirationPast,
  isApplicationExpiringSoon,
} from '@/views/campaigns/creators-tab/utils/isApplicationExpiringSoon';
import ExtendDeadlineModal from '@/components/extend-deadline-modal';
import styles from './ApplicationExpirationCell.module.scss';

type ApplicationExpirationCellProps = {
  creator: CampaignCreatorRow;
};

function renderExpirationStatus(
  canExtend: boolean,
  expirationHasBeenExtended: boolean
) {
  if (canExtend) {
    return (
      <Label
        size="sm"
        component="span"
        color={designSystemToken('semantic.fg.primary')}
        className={styles.extendLabel}
      >
        {translate('Extend')}
      </Label>
    );
  }

  if (expirationHasBeenExtended) {
    return (
      <Label
        size="sm"
        component="span"
        color={designSystemToken('semantic.fg.secondary')}
        className={styles.extended}
        data-testid="application-expiration-extended"
      >
        {translate('Extended')}
      </Label>
    );
  }

  return null;
}

function ApplicationExpirationCell({creator}: ApplicationExpirationCellProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {expiresAt, expirationHasBeenExtended, paidGigId} = creator;

  const stopRowClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleExtendClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsModalOpen(true);
  };

  if (!expiresAt) {
    return (
      <div className={styles.cell}>
        <span data-testid="application-expiration-date">
          <Body size="md">-</Body>
        </span>
      </div>
    );
  }

  const isPast = isApplicationExpirationPast(expiresAt);
  const isSoon = isApplicationExpiringSoon(expiresAt);
  const dateLabel = formattedDate(
    new Date(expiresAt),
    MONTH_SHORT_DAY_YEAR_FORMAT
  );
  const dateText = isPast
    ? translate('Expired {date}', {date: dateLabel})
    : dateLabel;
  const canExtend = canExtendApplicationExpiration(creator);
  const statusEl = renderExpirationStatus(canExtend, expirationHasBeenExtended);

  const dateEl = (
    <span className={styles.date} data-testid="application-expiration-date">
      <Body
        size="md"
        component="span"
        color={isSoon ? designSystemToken('semantic.fg.danger') : undefined}
      >
        {dateText}
      </Body>
    </span>
  );

  return (
    <>
      {canExtend ? (
        <Button
          type="button"
          appearance="neutral"
          mode="plain"
          size="small"
          className={styles.cellButton}
          testID="extend-deadline-button"
          aria-label={translate('Extend deadline by 14 days for this creator')}
          onMouseDown={stopRowClick}
          onClick={handleExtendClick}
        >
          {dateEl}
          {statusEl}
        </Button>
      ) : (
        <div className={styles.cell}>
          {dateEl}
          {statusEl}
        </div>
      )}
      {isModalOpen && paidGigId ? (
        <span
          role="presentation"
          onClick={stopRowClick}
          onKeyDown={stopRowClick}
        >
          <ExtendDeadlineModal
            open
            onClose={() => setIsModalOpen(false)}
            paidGigId={paidGigId}
            currentExpiresAt={expiresAt}
          />
        </span>
      ) : null}
    </>
  );
}

export default ApplicationExpirationCell;
